import { Timestamp, deleteField, collection, getDocs, query, where } from 'firebase/firestore'
import { db } from '@/firebase'

export const COUPON_APPLIES_TO = ['items', 'total', 'delivery']
export const COUPON_CHANNELS = ['app', 'menu']

/** missing applies_to → old behaviour from offer_in_total */
export function resolveCouponAppliesTo(raw) {
  const v = String(raw?.applies_to || '').trim()
  if (COUPON_APPLIES_TO.includes(v)) return v
  return raw?.offer_in_total === true ? 'total' : 'delivery'
}

export function couponAppliesToLabel(v) {
  if (v === 'items') return 'الأكل فقط'
  if (v === 'total') return 'إجمالي الطلب (أكل + توصيل)'
  return 'التوصيل فقط'
}

export function isOwnerCoupon(coupon) {
  return Boolean(String(coupon?.owner_entity_id || '').trim())
}

/** قراءة كوبون من Firestore — أسماء الحقول كما في تطبيق العميل */
export function normCouponFromDoc(docId, data) {
  const raw = data && typeof data === 'object' ? data : {}
  let endDate = null
  if (raw.end_date?.toDate) {
    endDate = raw.end_date.toDate()
  } else if (raw.end_date instanceof Date) {
    endDate = raw.end_date
  }

  return {
    id: String(docId || raw.id || '').trim(),
    code: String(raw.code || '').trim(),
    where: String(raw.where || '').trim(),
    discount: String(raw.discount ?? '0').trim(),
    upto: Math.max(0, Math.floor(Number(raw.upto) || 0)),
    offer_in_total: raw.offer_in_total === true,
    for_all_res: raw.for_all_res !== false,
    based_on_count: raw.based_on_count === true || raw.baesd_on_count === true,
    based_on_date: raw.based_on_date === true,
    count: Math.max(0, Math.floor(Number(raw.count) || 0)),
    end_date: endDate,
    restaurants: Array.isArray(raw.restaurants) ? raw.restaurants.map(String) : [],
    users: Array.isArray(raw.users) ? raw.users.map(String) : [],
    owner_entity_id: String(raw.owner_entity_id || '').trim(),
    owner_entity_type: String(raw.owner_entity_type || '').trim(),
    funded_by: raw.funded_by === 'restaurant' ? 'restaurant' : 'app',
    applies_to: resolveCouponAppliesTo(raw),
    channels: Array.isArray(raw.channels) ? raw.channels.map(String) : [...COUPON_CHANNELS],
    /** null = every service (field missing) */
    services: Array.isArray(raw.services) ? raw.services.map(String) : null,
    active: raw.active !== false,
    min_order: Math.max(0, Number(raw.min_order) || 0),
    created_by: String(raw.created_by || '').trim(),
    created_at: raw.created_at?.toDate ? raw.created_at.toDate() : null
  }
}

/**
 * A code must be unique in its city (the app and the menu look codes up by
 * code + city). Returns the other coupon using it, or null.
 */
export async function findCodeTakenInCity(code, city, exceptId = '') {
  const c = String(code || '').trim().toUpperCase()
  const w = String(city || '').trim()
  if (!c || !w) return null
  const snap = await getDocs(
    query(collection(db, 'Coupons'), where('code', '==', c), where('where', '==', w))
  )
  const other = snap.docs.find((d) => d.id !== String(exceptId || '').trim())
  return other ? normCouponFromDoc(other.id, other.data()) : null
}

export async function isCodeTakenInCity(code, city, exceptId = '') {
  return Boolean(await findCodeTakenInCity(code, city, exceptId))
}

export function couponUsageCount(coupon) {
  return Array.isArray(coupon?.users) ? coupon.users.length : 0
}

export function couponValidityLabel(coupon) {
  if (coupon?.based_on_date) {
    if (!coupon.end_date) return 'تاريخ — بدون end_date'
    return `حتى ${formatCouponDate(coupon.end_date)}`
  }
  if (coupon?.based_on_count) {
    const used = couponUsageCount(coupon)
    const max = coupon.count || 0
    return `${used} / ${max} استخدام`
  }
  return '—'
}

export function formatCouponDate(d) {
  if (!d) return '—'
  try {
    return new Intl.DateTimeFormat('ar-EG', {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(d instanceof Date ? d : new Date(d))
  } catch {
    return String(d)
  }
}

function parsePositiveInt(value, fallback = 0) {
  const n = Math.floor(Number(String(value ?? '').replace(/\D/g, '') || 0))
  return Number.isFinite(n) && n >= 0 ? n : fallback
}

/**
 * @param {object} form
 * @param {{ existingUsers?: string[], docId?: string }} opts
 */
export function buildCouponWritePayload(form, opts = {}) {
  const existingUsers = Array.isArray(opts.existingUsers) ? opts.existingUsers : []
  const resetUsers = form.resetUsers === true
  const basedOnCount = form.based_on_count === true

  const payload = {
    code: String(form.code || '').trim().toUpperCase(),
    where: String(form.where || '').trim(),
    discount: String(parsePositiveInt(form.discount, 0)),
    upto: parsePositiveInt(form.upto, 0),
    offer_in_total: form.offer_in_total === true,
    for_all_res: form.for_all_res === true,
    based_on_count: basedOnCount,
    based_on_date: form.based_on_date === true,
    count: basedOnCount ? Math.max(1, parsePositiveInt(form.count, 1)) : 0,
    restaurants: form.for_all_res
      ? []
      : (Array.isArray(form.restaurantIds) ? form.restaurantIds.map(String) : []),
    users: resetUsers ? [] : existingUsers
  }

  const appliesTo = COUPON_APPLIES_TO.includes(form.applies_to)
    ? form.applies_to
    : form.offer_in_total === true
      ? 'total'
      : 'delivery'
  payload.applies_to = appliesTo
  // old app versions only know offer_in_total: closest behaviour for items/total
  payload.offer_in_total = appliesTo !== 'delivery'
  payload.funded_by = form.funded_by === 'restaurant' ? 'restaurant' : 'app'
  payload.active = form.active !== false
  payload.min_order = parsePositiveInt(form.min_order, 0)
  const channels = Array.isArray(form.channels)
    ? form.channels.filter((c) => COUPON_CHANNELS.includes(c))
    : [...COUPON_CHANNELS]
  payload.channels = channels.length ? channels : [...COUPON_CHANNELS]

  const ownerId = String(form.owner_entity_id || '').trim()
  if (ownerId) {
    payload.owner_entity_id = ownerId
    payload.owner_entity_type = String(form.owner_entity_type || '').trim()
    payload.for_all_res = false
    payload.restaurants = [ownerId]
    payload.services = ['restaurant']
  } else if (Array.isArray(form.services) && form.services.length) {
    payload.services = form.services.map(String)
  }

  if (!opts.isUpdate) {
    payload.created_at = Timestamp.now()
    if (form.created_by) payload.created_by = String(form.created_by)
  }

  if (opts.isUpdate) {
    payload.baesd_on_count = deleteField()
  }

  if (opts.docId) {
    payload.id = String(opts.docId).trim()
  }

  if (form.based_on_date && form.end_date) {
    const d = form.end_date instanceof Date ? form.end_date : new Date(form.end_date)
    if (!Number.isNaN(d.getTime())) {
      payload.end_date = Timestamp.fromDate(d)
    }
  } else if (opts.isUpdate) {
    payload.end_date = deleteField()
  }

  return payload
}

export const COUPON_ENTITY_KINDS = [
  { value: 'restaurants', label: 'مطاعم', collection: 'categories', categoryType: 1 },
  { value: 'pharmacies', label: 'صيدليات', collection: 'categories', categoryType: 3 },
  { value: 'shops', label: 'محلات', collection: 'shops', shopLab: false }
]

export function couponEntityKindLabel(value) {
  return COUPON_ENTITY_KINDS.find((k) => k.value === value)?.label || 'كيانات'
}

export function emptyCouponForm() {
  return {
    id: '',
    code: '',
    where: '',
    entityKind: 'restaurants',
    discount: '10',
    upto: '15',
    offer_in_total: false,
    for_all_res: true,
    based_on_count: true,
    based_on_date: false,
    count: '1',
    end_date: null,
    restaurantIds: [],
    resetUsers: false,
    applies_to: 'delivery',
    funded_by: 'app',
    active: true,
    min_order: '0',
    channels: [...COUPON_CHANNELS]
  }
}

export function couponFormFromRow(row, entityKind = 'restaurants') {
  return {
    id: row.id,
    code: row.code,
    where: row.where,
    entityKind,
    discount: String(row.discount ?? '0'),
    upto: String(row.upto ?? '0'),
    offer_in_total: row.offer_in_total,
    for_all_res: row.for_all_res,
    based_on_count: row.based_on_count,
    based_on_date: row.based_on_date,
    count: String(row.count ?? '1'),
    end_date: row.end_date ? new Date(row.end_date) : null,
    restaurantIds: [...(row.restaurants || [])],
    resetUsers: false,
    applies_to: row.applies_to || (row.offer_in_total ? 'total' : 'delivery'),
    funded_by: row.funded_by || 'app',
    active: row.active !== false,
    min_order: String(row.min_order ?? 0),
    channels: Array.isArray(row.channels) ? [...row.channels] : [...COUPON_CHANNELS],
    owner_entity_id: row.owner_entity_id || '',
    owner_entity_type: row.owner_entity_type || '',
    services: Array.isArray(row.services) ? [...row.services] : null
  }
}
